// Copyright (c) StrangeBee 2024

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import {
    CustomizeOutput,
    WidgetOutput,
    DescribeFieldAttribute,
    ObjectTypeV1,
    CustomStatusOutput,
    UserOutput,
    ChartOutput,
} from 'thehive-sdk';
import client from '@Client';
import { useListAllFields } from '@Queries/describeQueries';
import { Column, ColumnConfig } from '@ant-design/charts';
import { WidgetError } from '@Organisms/Dashboard/DashboardDetails/WidgetError';
import { colorFormat, dateFormat, filterOnClick, parseDurationField, parsePeriod } from './utils';
import { useDashboard } from '@Hooks/useDashboard';
import { SearchScopeType } from '@Types';
import useStorageSearch from '@Hooks/useStorageSearch';
import { useHistory } from 'react-router';
import RouteBuilder from '@Services/RouteBuilder';
import { LanguageSwitcherContext } from '@Lang/LanguageSwitcherContext';
import _ from 'lodash';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useLocalMessage, UseLocalMessageType } from '@Helpers/locale';
import { ActionWidget } from '@Molecules/Widgets/ActionWidget';
import { downloadFile } from '@Helpers/download';
import { slugify } from '@Helpers/string';
import useListQuery from '@Hooks/useListQuery';
import { computeListSelector } from '@Organisms/CustomStatus/selector';
import { RandomAntColors } from '@Constants/colors';
import { useListAllUsers } from '@Queries/userQueries';
import { WidgetLoading } from '@Organisms/Dashboard/DashboardDetails/WidgetLoading/WidgetLoading';
import { UseQueryResult } from 'react-query';

import './Chart.scss';

type BarQueryField = {
    count: number;
    _key: string;
};
type BarQueryReturn = {
    _key: number;
    field: BarQueryField[] | null;
}[];

interface BarChartOutput {
    name: string;
    value: number;
    time: string;
    type?: string;
    color?: string;
    login?: string;
    key?: string;
    label?: string;
    [key: string]: any;
}

function getKeys(barData: BarQueryReturn): string[] {
    return Array.from(
        new Set(
            barData?.reduce((acc, { field }) => [...acc, ...(field?.map(({ _key }) => _key) || [])], [] as string[]),
        ),
    );
}

const prepareChartData = (
    barData: BarQueryReturn,
    customize: CustomizeOutput[],
    describeField: DescribeFieldAttribute | undefined,
    userList: UserOutput[] | undefined,
    i: UseLocalMessageType,
): BarChartOutput[] => {
    const fields: BarChartOutput[] = [];
    const keys = getKeys(barData);

    barData?.forEach(({ _key, field }) => {
        keys.forEach((key) => {
            const { count = 0 } = field?.find(({ _key }) => _key === key) || {};

            const index =
                describeField?.type === 'user'
                    ? userList?.findIndex(({ login }) => login === key)
                    : describeField?.values.findIndex((value) => `${value}` === `${key}`);

            const name =
                describeField?.type === 'user'
                    ? userList?.find(({ login }) => login === key)?.name || key
                    : index !== undefined && index !== -1 && index < customize.length
                    ? customize[index].label || ''
                    : key;

            const obj = {
                time: new Date(_key).toISOString(),
                name,
                login: describeField?.type === 'user' ? key : undefined,
                value: count,
                color:
                    index !== undefined && index !== -1 && index < customize.length ? customize[index].color || '' : '',
                key: index !== undefined && index !== -1 ? key : undefined,
                label: parseDurationField(describeField?.name || '', count, i),
            };

            fields.push(obj);
        });
    });

    return fields;
};

interface BarWidgetProps {
    widget: WidgetOutput;
    query?: UseQueryResult<ChartOutput[]>;
}

export const BarWidget = React.memo(({ widget, query }: BarWidgetProps): ReactElement => {
    const i = useLocalMessage();

    const [chart, setChart] = useState<any>();

    const { data: userList = [] } = useListAllUsers();
    const { data: describeData } = useListAllFields();
    const history = useHistory();
    const { setSearchActiveScope, setSearchFilters, setSearchKeyword } = useStorageSearch();
    const {
        entity,
        interval = '1d',
        field = '',
        filters = [],
        periodDateField = '',
        stacked = false,
        log = false,
        customize = [],
    } = widget.options || {};
    const { dashboard } = useDashboard();
    const { language } = useContext(LanguageSwitcherContext);

    const {
        definition: { period, customPeriod },
    } = dashboard;
    const { from, to } = parsePeriod(customPeriod, period);

    const periodFilters =
        period !== 'all'
            ? [
                  {
                      field: periodDateField,
                      type: 'date',
                      operator: 'custom',
                      values: [from, to],
                      describeAttribute: describeData?.[entity]?.attributes.find(
                          ({ name }) => name === periodDateField,
                      ) as any,
                  },
              ]
            : [];

    const { data: customStatusList = [], refetch: refetchCustomStatusList } = useListQuery<CustomStatusOutput[]>({
        controller: client.CustomStatusController,
        queryParams: {
            selector: computeListSelector({ entity: entity as any }),
        },
        manual: true,
        fetchOnStart: false,
    });

    useEffect(() => {
        if (customStatusList.length) {
            return;
        }

        if (
            [ObjectTypeV1.CASE, ObjectTypeV1.ALERT, ObjectTypeV1.CASE_STATUS, ObjectTypeV1.ALERT_STATUS].includes(
                entity,
            )
        ) {
            refetchCustomStatusList();
        }
    }, [customStatusList.length, entity, refetchCustomStatusList]);

    const customizeParsed = useMemo(() => {
        let index = 0;

        return customize.map(({ color, ...field }) => {
            return { ...field, color: color || RandomAntColors[index++] };
        });
    }, [customize]);

    if (query?.isLoading) {
        return (
            <div className="widget__empty">
                <WidgetLoading />
            </div>
        );
    }

    if (query?.status === 'error') {
        return <WidgetError />;
    }

    if (_.isEmpty(query?.data) || !query?.data) {
        return (
            <div className="widget__empty">
                <DisplayEmptyData label={i('dashboard.widget.empty')} />
            </div>
        );
    }

    const dataTreated = prepareChartData(
        query?.data as BarQueryReturn,
        customizeParsed,
        describeData?.[entity]?.attributes.find(({ name }) => name === field),
        userList,
        i,
    );

    const config: ColumnConfig = {
        data: dataTreated,

        isGroup: !stacked,
        isStack: stacked,

        xAxis: {
            label: {
                formatter: (date) =>
                    dateFormat({
                        date,
                        interval,
                        language,
                    }),
            },
        },

        yAxis: {
            type: log ? 'log' : undefined,
            label: {
                formatter: (value) => (log && (parseInt(value) || 0) === 0 ? `log 0` : value),
            },
        },

        xField: 'time',
        yField: 'value',
        seriesField: 'name',

        legend: {
            slidable: false,
            flipPage: false,
            position: 'bottom',
            label: { align: 'center' },
            layout: 'vertical',
            itemHeight: 6,
            itemSpacing: 1,
            maxItemWidth: 500,
        },

        color: customize?.length
            ? (datum) =>
                  customizeParsed.find(({ label, name }) => label === datum?.name || name === datum?.name)?.color || ''
            : colorFormat({
                  field,
                  customStatusList,
                  entity,
              }),

        tooltip: {
            shared: true,
            title: (date) => dateFormat({ date, interval, language }),
            fields: ['label', 'value', 'name'],
            formatter: (entry) => ({
                name: entry.name,
                value: entry.label,
            }),
        },

        className: 'chart',
    };

    return (
        <div className="widget__display">
            <Column
                {...config}
                onReady={(plot) => {
                    setChart(plot);
                    plot.on('plot:click', (evt: any) => {
                        const { x, y } = evt;
                        const tooltipData = plot.chart
                            .getTooltipItems({ x, y })
                            .reverse()
                            .find((data) => x > data.x);

                        const name =
                            tooltipData.find(Boolean)?.data?.login ||
                            (tooltipData.find(Boolean)?.data?.key !== undefined
                                ? tooltipData.find(Boolean)?.data?.key
                                : tooltipData.find(Boolean)?.name);

                        if (name === undefined) {
                            return;
                        }

                        filterOnClick({
                            value: tooltipData?.data?.login || tooltipData?.name,
                            entity,
                            field,
                            describeData,
                            onClick: (value, describeField) => {
                                setSearchFilters(entity as SearchScopeType, [
                                    ...filters,
                                    ...periodFilters,
                                    {
                                        field,
                                        operator: 'any',
                                        values: [value],
                                        type: describeField?.type || 'string',
                                        describeAttribute: describeData?.[entity]?.attributes.find(
                                            ({ name }) => name === field,
                                        ) as any,
                                    },
                                ]);
                                setSearchKeyword(entity as SearchScopeType, '');
                                setSearchActiveScope(entity as SearchScopeType);
                                history.push(RouteBuilder.compute('search.default'));
                            },
                        });
                    });
                }}
            />

            <ActionWidget
                onDownloadImage={() => chart.downloadImage(slugify(widget.options?.title || ''))}
                onDownloadCSV={() => {
                    const data = query?.data as BarQueryReturn;
                    const describeField = describeData?.[entity]?.attributes.find(({ name }) => name === field);
                    const keys = getKeys(data);

                    const header = `Date,${keys
                        .map((key) => {
                            const index = describeField?.values.indexOf(parseInt(key));
                            return index !== undefined && index !== -1 ? describeField?.labels[index] || key : key;
                        })
                        .join(',')}`;
                    const body = data
                        ?.map(({ _key, field }) => {
                            const date = dateFormat({
                                date: new Date(_key).toISOString(),
                                interval,
                                language,
                            });
                            return `${date},${keys
                                .map((key) => field?.find(({ _key }) => _key === key)?.count || 0)
                                .join(',')}`;
                        })
                        .join('\n');
                    const tab = header + '\n' + body;

                    downloadFile(`${widget.options?.title || ''}.csv`, tab, 'text/csv');
                }}
            />
        </div>
    );
});
